import { useCallback, useMemo, useState } from 'react';

/**
 * Orden por columna + búsqueda sobre todos los datos de una tabla, en el
 * cliente. Pedido del cliente (Tesorería, Cierres de Caja, Histórico): ordenar
 * asc/desc cualquier columna y que "Buscar" encuentre también los importes.
 *
 * Los importes se comparan por sus dígitos: "1.234,56", "1234,56", "1234.56"
 * y "$ 1.234,56" encuentran el mismo 1234.56 en AR y en MX (que usa el punto
 * como decimal), sin adivinar el locale de lo que escribió el usuario.
 */

export type DireccionOrden = 'asc' | 'desc';
export type Orden<K extends string> = { key: K; dir: DireccionOrden } | null;

const sinAcentos = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Lo que escribió el usuario, listo para comparar. `null` = sin búsqueda. */
export type Busqueda = { texto: string; digitos: string | null };

export function prepararBusqueda(raw: string): Busqueda | null {
  const texto = sinAcentos(raw.trim());
  if (!texto) return null;
  // Parece un importe: sólo dígitos, separadores, signo, $ y espacios.
  const esNumero = /^[-+$\s]*\d[\d.,\s]*$/.test(texto);
  return { texto, digitos: esNumero ? texto.replace(/\D/g, '') : null };
}

function digitosDe(v: unknown): string {
  // Siempre con 2 decimales: 1234.5 → "123450", así "1234,50" y "1234,5" lo encuentran.
  if (typeof v === 'number') return Number.isFinite(v) ? Math.abs(v).toFixed(2).replace(/\D/g, '') : '';
  return String(v).replace(/\D/g, '');
}

/** ¿Alguno de los valores de la fila coincide con la búsqueda? */
export function coincideBusqueda(valores: readonly unknown[], b: Busqueda | null): boolean {
  if (!b) return true;
  return valores.some((v) => {
    if (v == null || v === '') return false;
    if (typeof v !== 'number' && sinAcentos(String(v)).includes(b.texto)) return true;
    if (typeof v === 'number' && String(v).includes(b.texto)) return true;
    return b.digitos != null && b.digitos !== '' && digitosDe(v).includes(b.digitos);
  });
}

/**
 * Número escrito en es-AR ("1.234,56") o es-MX ("1,234.56"), con o sin
 * moneda alrededor. `null` si no es un número.
 */
export function parseNumeroFlexible(raw: string): number | null {
  const t = raw.trim();
  // Sólo importes: signo, moneda opcional ($, US$, ARS…), dígitos y %.
  if (!/^[-−+(]?\s*(?:US\$|\$|USD|ARS|MXN)?\s*[-−]?\s*\d[\d.,]*\s*(?:USD|ARS|MXN|%)?\)?$/i.test(t)) return null;
  const negativo = /[-−(]/.test(t);
  let s = t.replace(/[^\d.,]/g, '');
  const ultPunto = s.lastIndexOf('.');
  const ultComa = s.lastIndexOf(',');
  if (ultPunto >= 0 && ultComa >= 0) {
    // El separador que aparece último es el decimal.
    s = ultComa > ultPunto ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  } else if (ultComa >= 0) {
    s = /^\d{1,3}(,\d{3})+$/.test(s) && s.split(',').length > 2 ? s.replace(/,/g, '') : s.replace(/,/g, '.');
  } else if (ultPunto >= 0 && /^\d{1,3}(\.\d{3})+$/.test(s)) {
    s = s.replace(/\./g, ''); // "1.234" / "12.345.678": miles
  }
  const n = Number(s);
  return Number.isFinite(n) ? (negativo ? -n : n) : null;
}

/** Valor comparable de una celda: número, fecha (dd/mm/aaaa [hh:mm]) o texto. */
export function valorOrden(v: unknown): number | string | null {
  if (v == null || v === '' || v === '—') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v).trim();
  const f = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?/);
  if (f) return Number(`${f[3]}${f[2].padStart(2, '0')}${f[1].padStart(2, '0')}${(f[4] ?? '0').padStart(2, '0')}${f[5] ?? '00'}`);
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);
  if (iso) return Number(`${iso[1]}${iso[2]}${iso[3]}${iso[4] ?? '00'}${iso[5] ?? '00'}`);
  const n = parseNumeroFlexible(s);
  if (n != null) return n;
  return sinAcentos(s);
}

const colator = new Intl.Collator('es', { numeric: true, sensitivity: 'base' });

/** Orden estable; los vacíos van siempre al final, en las dos direcciones. */
export function ordenarFilas<T, K extends string>(
  rows: readonly T[],
  orden: Orden<K>,
  valor: (row: T, key: K) => unknown,
): T[] {
  if (!orden) return [...rows];
  const signo = orden.dir === 'asc' ? 1 : -1;
  return rows
    .map((row, i) => ({ row, i, v: valorOrden(valor(row, orden.key)) }))
    .sort((a, b) => {
      if (a.v == null || b.v == null) return a.v == null && b.v == null ? a.i - b.i : a.v == null ? 1 : -1;
      // Columna mixta: los números antes que el texto.
      const c =
        typeof a.v === 'number' && typeof b.v === 'number'
          ? a.v - b.v
          : typeof a.v === 'number'
            ? -1
            : typeof b.v === 'number'
              ? 1
              : colator.compare(a.v, b.v);
      return c !== 0 ? c * signo : a.i - b.i;
    })
    .map((x) => x.row);
}

/**
 * Estado de orden de una tabla. Cada click en un encabezado: asc → desc →
 * sin orden (vuelve al orden que trae el server). `valor` tiene que ser
 * estable (definido fuera del componente o con useCallback).
 */
export function useOrdenTabla<T, K extends string>(
  rows: readonly T[],
  valor: (row: T, key: K) => unknown,
) {
  const [orden, setOrden] = useState<Orden<K>>(null);
  const ordenadas = useMemo(() => ordenarFilas(rows, orden, valor), [rows, orden, valor]);
  const alternar = useCallback(
    (key: K) =>
      setOrden((o) =>
        !o || o.key !== key ? { key, dir: 'asc' } : o.dir === 'asc' ? { key, dir: 'desc' } : null,
      ),
    [],
  );
  return { ordenadas, orden, alternar };
}
